import { IGeolocationPhoto } from '../../../../services/GeolocationService/geolocation.types';

/**
 * Formata uma coordenada geográfica para exibição
 * @param coordinate - Valor numérico da coordenada (latitude ou longitude)
 * @param decimals - Número de casas decimais (padrão: 6)
 * @returns String formatada ou fallback para coordenadas inválidas
 */
export const formatCoordinate = (coordinate: number | null | undefined, decimals = 6): string => {
  if (coordinate === null || coordinate === undefined || isNaN(coordinate)) {
    return 'N/A';
  }

  return coordinate.toFixed(decimals);
};

/**
 * Valida se as coordenadas são válidas para exibição
 * @param latitude - Valor da latitude
 * @param longitude - Valor da longitude
 * @returns true se ambas as coordenadas são válidas
 */
export const hasValidCoordinates = (
  latitude: number | null | undefined,
  longitude: number | null | undefined
): boolean => {
  return (
    latitude !== null &&
    latitude !== undefined &&
    longitude !== null &&
    longitude !== undefined &&
    !isNaN(latitude) &&
    !isNaN(longitude) &&
    latitude >= -90 &&
    latitude <= 90 &&
    longitude >= -180 &&
    longitude <= 180
  );
};

/**
 * Retorna coordenadas formatadas como objeto
 * @param latitude - Valor da latitude
 * @param longitude - Valor da longitude
 * @returns Objeto com coordenadas formatadas
 */
export const getFormattedCoordinates = (latitude: number | null | undefined, longitude: number | null | undefined) => {
  return {
    latitude: formatCoordinate(latitude),
    longitude: formatCoordinate(longitude),
    isValid: hasValidCoordinates(latitude, longitude),
  };
};

/**
 * Calcula a distância entre duas coordenadas usando a fórmula de Haversine
 * @param lat1 - Latitude do ponto 1
 * @param lon1 - Longitude do ponto 1
 * @param lat2 - Latitude do ponto 2
 * @param lon2 - Longitude do ponto 2
 * @returns Distância em metros
 */
export const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 6371e3; // Raio da Terra em metros
  const φ1 = (lat1 * Math.PI) / 180;
  const φ2 = (lat2 * Math.PI) / 180;
  const Δφ = ((lat2 - lat1) * Math.PI) / 180;
  const Δλ = ((lon2 - lon1) * Math.PI) / 180;

  const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) + Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

export interface IClusteredMarker {
  latitude: number;
  longitude: number;
  photos: IGeolocationPhoto[];
  count: number;
}

/**
 * Agrupa marcadores próximos baseado em distância mínima
 * @param markers - Array de marcadores com geolocalização
 * @param minDistance - Distância mínima em metros para agrupar (padrão: 100m)
 * @returns Array de marcadores agrupados
 */
export const clusterMarkers = (markers: IGeolocationPhoto[], minDistance = 100): IClusteredMarker[] => {
  if (markers.length === 0) return [];

  const clusters: IClusteredMarker[] = [];
  const processed = new Set<number>();

  markers.forEach((marker, index) => {
    if (processed.has(index)) return;
    if (!hasValidCoordinates(marker.latitude, marker.longitude)) return;

    // Type guard: após validação, sabemos que latitude e longitude não são null
    const markerLat = marker.latitude as number;
    const markerLng = marker.longitude as number;

    const cluster: IClusteredMarker = {
      latitude: markerLat,
      longitude: markerLng,
      photos: [marker],
      count: 1,
    };

    // Encontra marcadores próximos
    markers.forEach((otherMarker, otherIndex) => {
      if (index === otherIndex || processed.has(otherIndex)) return;
      if (!hasValidCoordinates(otherMarker.latitude, otherMarker.longitude)) return;

      // Type guard: após validação, sabemos que latitude e longitude não são null
      const otherLat = otherMarker.latitude as number;
      const otherLng = otherMarker.longitude as number;

      const distance = calculateDistance(markerLat, markerLng, otherLat, otherLng);

      if (distance <= minDistance) {
        cluster.photos.push(otherMarker);
        cluster.count++;
        processed.add(otherIndex);
      }
    });

    processed.add(index);
    clusters.push(cluster);
  });

  return clusters;
};

/**
 * Copia texto para o clipboard
 * @param text - Texto a ser copiado
 * @returns Promise que resolve quando a cópia é bem-sucedida
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    // Fallback para navegadores mais antigos
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);

    return successful;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

/**
 * Formata coordenadas para cópia (lat, lng)
 * @param latitude - Valor da latitude
 * @param longitude - Valor da longitude
 * @returns String formatada para clipboard
 */
export const formatCoordinatesForClipboard = (latitude: number, longitude: number): string => {
  return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
};
